import {useEffect, useRef} from 'react';

import {useCMSBranchId} from '../utils/activeCMSBranchId';

type QueryResult = {
  isSuccess: boolean;
  isError: boolean;
  isPreviousData: boolean;
  fetchStatus: 'fetching' | 'paused' | 'idle';
};

/**
 * The CMS branch a CMS item query reads, and `keepPreviousData` for it. The
 * query puts `cmsBranchId` in its key and passes the same value to its fetch,
 * so the two can't disagree.
 *
 * `keepPreviousData` is for CMS item queries, whose keys include the branch.
 * Keeps the previous key's data while the next loads, except after a branch
 * switch: until the new branch's data loads, the previous data is the other
 * branch's items. Pass the query results to `track` on every render.
 *
 * A query that is disabled or failed has nothing more to load, so it doesn't
 * hold the switch open.
 */
export function useKeepPreviousCMSBranchData(): {
  cmsBranchId: string | null;
  keepPreviousData: boolean;
  track: (results: QueryResult[]) => void;
} {
  const cmsBranchId = useCMSBranchId();
  const loadedCMSBranchId = useRef(cmsBranchId);
  const settled = useRef(false);

  // Written after commit, so a render React discards can't mark the branch
  // loaded.
  useEffect(() => {
    if (settled.current) {
      loadedCMSBranchId.current = cmsBranchId;
    }
  });

  return {
    cmsBranchId,
    keepPreviousData: loadedCMSBranchId.current === cmsBranchId,
    track: (results) => {
      settled.current = results.every(
        (result) =>
          !result.isPreviousData &&
          (result.isSuccess || result.isError || result.fetchStatus === 'idle')
      );
    },
  };
}
